import { apiRequest } from './client'
import type { StudioCanvasGraph } from './studio-production'

export interface CreativeWorkspace {
  id: number
  public_id: string
  user_id?: number
  title: string
  summary: string
  cover_url: string
  storage_mode: string
  storage_prefix?: string
  storage_config?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface CreativeWorkspaceList {
  items: CreativeWorkspace[]
  total: number
  limit: number
  offset: number
}

export function listCreativeWorkspaces(limit = 20, offset = 0): Promise<CreativeWorkspaceList> {
  return apiRequest(`/api/v1/studio/creative-workspaces?limit=${limit}&offset=${offset}`)
}

export function createCreativeWorkspace(body: {
  title: string
  summary?: string
  cover_url?: string
}): Promise<CreativeWorkspace> {
  return apiRequest('/api/v1/studio/creative-workspaces', { method: 'POST', body })
}

export function getCreativeWorkspace(publicId: string): Promise<CreativeWorkspace> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(publicId)}`)
}

export function patchCreativeWorkspace(
  publicId: string,
  body: Partial<{ title: string; summary: string; cover_url: string }>,
): Promise<CreativeWorkspace> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(publicId)}`, {
    method: 'PATCH',
    body,
  })
}

export function deleteCreativeWorkspace(publicId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(publicId)}`, { method: 'DELETE' })
}

export function getCreativeGraph(workspaceId: string): Promise<StudioCanvasGraph> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(workspaceId)}/graph`)
}

export function saveCreativeGraph(
  workspaceId: string,
  body: {
    nodes: unknown[]
    edges: unknown[]
    viewport: { x: number; y: number; zoom: number }
    if_match_revision?: number
  },
): Promise<StudioCanvasGraph> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(workspaceId)}/graph`, {
    method: 'PUT',
    body,
  })
}

export interface StudioCreativeListing {
  listing_public_id: string
  listing_id: number
  status: string
  price_points: number
  license_mode: string
  graph_revision: number
}

export function listCreativeListings(workspaceId: string, limit = 20): Promise<{ items: StudioCreativeListing[] }> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(workspaceId)}/listings?limit=${limit}`)
}

export function createCreativeListing(
  workspaceId: string,
  body: {
    title?: string
    summary?: string
    cover_url?: string
    price_points: number
    license_mode?: 'non_exclusive' | 'exclusive'
    clips: Array<{ canvas_node_id: string; take_version: number }>
  },
): Promise<StudioCreativeListing> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(workspaceId)}/listings`, {
    method: 'POST',
    body,
  })
}

export interface PublishCreativeToWorksResult {
  work_id: number
  work_public_id: string
  work_status: string
  episodes_synced: number
}

export interface CreativePublishClipRef {
  canvas_node_id: string
  take_version: number
}

export function publishCreativeToWorks(
  workspaceId: string,
  body: { publish?: boolean; visibility?: string; clips?: CreativePublishClipRef[] } = {},
): Promise<PublishCreativeToWorksResult> {
  return apiRequest(`/api/v1/studio/creative-workspaces/${encodeURIComponent(workspaceId)}/publish-to-works`, {
    method: 'POST',
    body,
  })
}

export function creativeBasePath(workspaceId: string): string {
  return `/studio/creative/${workspaceId}`
}
